import React from "react";
import PropTypes from "prop-types";
import * as Sentry from "@sentry/react";
import { Box, Grid, Typography } from "@mui/material";
import Warning from "@mui/icons-material/Warning";
import { useTheme } from "@mui/material/styles";

import { useAccountData } from "../../helpers/firebase";
import { fetchOurApi } from "../../helpers/utils/apiCalls";
import usePlatformLayoutContext from "../../helpers/contexts/platformLayout";
import { stripSensitiveUrlParams } from "../../helpers/otherHelpers";

const FeatureErrorFallback = ({ featureName, reportFiled }) => {
  const { palette } = useTheme();
  return (
    <Box
      p={3}
      display="flex"
      alignItems="center"
      justifyContent="center"
      sx={{
        minHeight: 120,
        bgcolor: palette.backgroundGray.light,
        borderRadius: 1
      }}
    >
      <Grid container direction="column" alignItems="center" spacing={1}>
        <Grid item>
          <Warning sx={{ color: palette.warning.main }} />
        </Grid>
        <Grid item>
          <Typography variant="body1" align="center">
            {featureName} is temporarily unavailable
          </Typography>
        </Grid>
        <Grid item>
          <Typography variant="body2" color="textSecondary" align="center">
            {reportFiled
              ? "Our team has been notified and is working on it."
              : "Please refresh the page or contact support if this continues."}
          </Typography>
        </Grid>
      </Grid>
    </Box>
  );
};

FeatureErrorFallback.propTypes = {
  featureName: PropTypes.string.isRequired,
  reportFiled: PropTypes.bool.isRequired
};

class FeatureErrorBoundaryClass extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, reportFiled: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, { componentStack }) {
    const { featureName, companyId, companyName, onError } = this.props;

    Sentry.captureException(error);

    fetchOurApi({
      path: "/errors/report",
      method: "POST",
      data: {
        featureName,
        companyId,
        companyName,
        message: error.message,
        stack: error.stack,
        componentStack,
        url: stripSensitiveUrlParams(window.location.href)
      }
    })
      .then(() => {
        this.setState({ reportFiled: true });
        if (typeof onError === "function") {
          onError();
        }
      })
      .catch((reportErr) => {
        Sentry.captureException(reportErr, {
          tags: { feature: "feature-error-boundary" },
          extra: { featureName, originalErrorMessage: error.message }
        });
      });
  }

  render() {
    const { hasError, reportFiled } = this.state;
    const { featureName, children } = this.props;
    if (!hasError) return children;
    return (
      <FeatureErrorFallback
        featureName={featureName}
        reportFiled={reportFiled}
      />
    );
  }
}

FeatureErrorBoundaryClass.propTypes = {
  featureName: PropTypes.string.isRequired,
  companyId: PropTypes.string,
  companyName: PropTypes.string,
  onError: PropTypes.func,
  children: PropTypes.node.isRequired
};

const FeatureErrorBoundary = ({ featureName, children }) => {
  const [{ id: companyId, name: companyName }] = useAccountData();
  const { activateSnackbar } = usePlatformLayoutContext() || {};

  const handleError = () => {
    if (!activateSnackbar) {
      return;
    }
    activateSnackbar({
      message: `${featureName} is temporarily unavailable. Our team has been notified and is working on it.`,
      severity: "warning"
    });
  };

  return (
    <FeatureErrorBoundaryClass
      featureName={featureName}
      companyId={companyId}
      companyName={companyName}
      onError={handleError}
    >
      {children}
    </FeatureErrorBoundaryClass>
  );
};

FeatureErrorBoundary.propTypes = {
  featureName: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired
};

export default FeatureErrorBoundary;
